import { observer } from "mobx-react-lite/dist"
import useStore from "reusable/mobx-store-utils"
import CreateClientAutocompleteStore, { CreateClientAutocompleteStoreProvider, useCreateClientAutocompleteStore } from "./CreateClientAutocompleteStore"
import CreateClientModal from "features/clients/create/CreateClientModal"
import GapAutocompleteSingle from "../../gap-components/autocomplete/single/GapAutocompleteSingle"
import { AdvisorClientDto } from '@gap/backend/app-services/advisor/client/models/AdvisorClientDto'
import { useCacheStore } from "cache/CacheStore"
import SkeletonLoader from "reusable/mui/skeleton/SkeletonLoader"
import { useEffect } from "react"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Divider from "@mui/material/Divider"
import Typography from "@mui/material/Typography"
import { themeConfig } from "@gap/shared/theme"
import SyncOutlined from "@mui/icons-material/SyncOutlined"
import AddOutlined from "@mui/icons-material/AddOutlined"
import { useGapAutocompleteSingleStore } from "features/gap-components/autocomplete/single/GapAutocompleteSingleStore"

export interface CreateClientAutocompleteParams {
   id?: string
   label?: string
   value: string
   onChange: (clientId: string) => void
   onNewClientCreated?: (client: AdvisorClientDto.idWithName) => void
   /**
    * Identifies which parent modal/screen owns this autocomplete, so that an XPM OAuth
    * round trip can land back on the same surface. Required to make "Check XPM"
    * reopen the parent after redirect; without it the user lands on the bare URL.
    */
   /** Open the options dropdown as soon as the field gains focus. */
   openOnFocus?: boolean
   /** Field density. Defaults to medium; pass 'small' for compact placements like inline header selectors. */
   size?: 'small' | 'medium'
   /** Cap (in px) for the dropdown list width, letting it be wider than the input. Defaults to matching the input. */
   dropdownMaxWidth?: number
   /** Animate the dropdown open/closed with the Grow transition (matches MUI Menu). Defaults to instant. */
   dropdownTransition?: boolean
}

export default observer(function CreateClientAutocomplete(props: CreateClientAutocompleteParams) {
   const { id, label } = props
   const cacheStore = useCacheStore()
   const createClientAutocompleteStore = useStore((sp) => new CreateClientAutocompleteStore(sp), { ...props, cacheStore })

   useEffect(() => {
      createClientAutocompleteStore.fields.clientId = props.value
   }, [createClientAutocompleteStore, props.value])

   return <CreateClientAutocompleteStoreProvider store={createClientAutocompleteStore}>
      <SkeletonLoader loading={cacheStore.clientsCacheStore.clientsLoader.isLoading}>
         {createClientAutocompleteStore.formHandler.clientId.bindInput(ip =>
            <GapAutocompleteSingle
               {...ip}
               id={id || "create-client-autocomplete"}
               options={createClientAutocompleteStore.clientOptions}
               label={label || 'Clients'}
               autoFocus
               openOnFocus={props.openOnFocus}
               size={props.size}
               dropdownMaxWidth={props.dropdownMaxWidth}
               dropdownTransition={props.dropdownTransition}
               onChange={createClientAutocompleteStore.onClientChange}
               footer={<CreateClientAutocompleteFooter />}
            />
         )}
      </SkeletonLoader>

      {createClientAutocompleteStore.createClientModalState.visible && <CreateClientModal
         modalState={createClientAutocompleteStore.createClientModalState}
         newClientName={createClientAutocompleteStore.newClient}
         showCreateClientTabOnOpen={createClientAutocompleteStore.showCreateClientTabOnOpen}
         onCreateClientCallback={createClientAutocompleteStore.onNewClientCreated} />}
   </CreateClientAutocompleteStoreProvider>
})

const CreateClientAutocompleteFooter = observer(function CreateClientAutocompleteFooter() {
   const createClientAutocompleteStore = useCreateClientAutocompleteStore()
   const gapAutocompleteSingleStore = useGapAutocompleteSingleStore()

   const createClientAutocompleteFooterStore = useStore(sp => ({
      onClick: () => {
         sp.createClientAutocompleteStore.openCreateClientModal(sp.gapAutocompleteSingleStore.inputValue)
      }
   }), { gapAutocompleteSingleStore, createClientAutocompleteStore })

   return <Box onMouseDown={createClientAutocompleteStore.preventBlur}>
      {createClientAutocompleteStore.hasClients && <Divider />}
      {/* Wrap as a unit at low widths: the button group drops onto its own line
          rather than squeezing the buttons until their labels wrap mid-word. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 2, py: 1 }}>
         <Typography variant="body2" sx={{ color: themeConfig.primary[600] }}>Don't see the client you're looking for?</Typography>
         <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: 'auto' }}>
            <Button variant="text" size="small" startIcon={<SyncOutlined />} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={createClientAutocompleteStore.openCreateClientModalWithXPM}>Check XPM</Button>
            <Button variant="text" size="small" startIcon={<AddOutlined />} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} onClick={createClientAutocompleteFooterStore.onClick}>Create client</Button>
         </Box>
      </Box>
   </Box>
})